import useAuth from '@byma/shared/hooks/useAuth'
import { array as yupArray, object as yupObject, string as yupString } from 'yup'
import useForm from '@byma/shared/hooks/useForm'
import FormSection from '@byma/shared/components/FormSection'
import SearchUser from './SearchUser'
import Upload from '@byma/shared/components/Upload'
import { useRouter } from 'next/router'
import useToastContext from "@byma/shared/hooks/useToastContext";
import useSWR, { useSWRConfig } from 'swr'
import dayjs from 'dayjs'
import fetcher from '@byma/shared/helpers/fetcher'
import Loader from '@byma/shared/components/Loader'
import Input from '@byma/shared/components/Input'
import { useState } from 'react'
import useFilters from '@byma/shared/hooks/useFilters'

function TicketTypeItem({ticketType, active=false}) {
    return (
        <div className={`flex items-center cursor-pointer box box-hover ${active && 'box-active'} gap-3`}>
            <input type="checkbox" checked={active} readOnly />
            { ticketType.partyId?.pictures?.photos[0] ? (
                <img src={ticketType.partyId.pictures.photos[0]} alt={ticketType.partyId.name} className="rounded-full w-12 h-12 object-cover" />
            ) : (
                <div className="w-12 h-12 rounded-full bg-gray-400 overflow-hidden" />
            ) }
            <div className="">
                <p className="text-md font-bold">{ticketType.name}</p>
                <p className="text-sm">{ticketType.partyId?.name}</p>
            </div>
        </div>
    )
}

function EventItem({event, active=false}) {
    return (
        <div className={`flex items-center cursor-pointer box box-hover ${active && 'box-active'} gap-3`}>
            <input type="checkbox" checked={active} readOnly />
            { event?.pictures?.photos[0] ? (
                <img src={event.pictures.photos[0]} alt={event.name} className="rounded-full w-12 h-12 object-cover" />
            ) : (
                <div className="w-12 h-12 rounded-full bg-gray-400 overflow-hidden" />
            ) }
            <div className="">
                <p className="text-md font-bold">{event.name}</p>
                <p className="text-sm">{dayjs(event.date).format('DD/MM/YYYY [às] hh:mm')}</p>
            </div>
        </div>
    )
}

function toQueryString(obj, forceKey=null) {
    if (!obj) return ''
    return Object.keys(obj).map(function (key) {
        if (typeof obj[key] === 'object') {
            return toQueryString(obj[key], key)
        }
        return (forceKey || key) + '=' + obj[key]
    }).join('&');
}

export default function NewGatewaySession({ }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const [newGatewaySessionOpen, setNewGatewaySessionOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()
    const { query, ...router } = useRouter()
    const { mutate } = useSWRConfig()
    const { queryString } = useFilters({...query, d:'all'})
    const [selectedEvents, setSelectedEvents] = useState([])
    const [selectedTicketTypes, setSelectedTicketTypes] = useState([])

    const [searchQuery, setSearchQuery] = useState('')

    const { data: parties } = useSWR(token ? [`/api/parties/getAll?search=${searchQuery}&perPage=20`, null, token] : null, fetcher)
    const { data: ticketTypes } = useSWR(token && selectedEvents.length > 0 ? [`/api/tickettypes/getAll?${toQueryString({partyIds: selectedEvents})}&perPage=20`, null, token] : null, fetcher)

    const { data: formData, setFieldValue, errors: formErrors, handleSubmit, resetErrors: resetFormErrors, getError } = useForm({
        initialValues: { name: '', userIds: [], parties: [] },
        schema: yupObject().shape({
            name: yupString()
            .required('Nome é um campo obrigatório'),
            userIds: yupArray(),
            parties: yupArray()

        }),
        onSubmit: handleSave

    })

    function handleSelectParty(party) {
        if (selectedEvents.indexOf(party._id) >= 0) {
            setSelectedEvents(selectedEvents.filter(e => e !== party._id))
        } else {
            setSelectedEvents([...selectedEvents, party._id])
        }
    }

    function handleSelectTicketType(ticketType) {
        if (selectedTicketTypes.indexOf(ticketType._id) >= 0) {
            setSelectedTicketTypes(selectedTicketTypes.filter(e => e !== ticketType._id))
        } else {
            setSelectedTicketTypes([...selectedTicketTypes, ticketType._id])
        }
    }

    async function handleSearch(e) {
        e.preventDefault()
        e.stopPropagation()
        setSearchQuery(e.target.value)
        return false
    }

    function renderError(key) {
        const error = getError(key)
        return error && <div className="text-red-500">{error}</div>
    }

    async function handleSave(values) {

        const body = {
            ...values,
            userIds: formData.userIds.map(i => i._id),
            parties: selectedEvents.map(eventId => ({
                partyId: eventId,
                ticketTypes: ticketTypes.result.filter(tt => selectedTicketTypes.indexOf(tt._id) >= 0 && tt.partyId === eventId).map(tt => tt._id)
            }))
        }
        setIsLoading(true)

        try {
            const res = await fetch('/api/gatewaySessions/create', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Auth': token
                },
                body: JSON.stringify(body),
            })
            if (res.status === 200) {
                const sessionCreated = await res.json()
                setNewGatewaySessionOpen(false)
                addToast('Sessão cadastrada com sucesso')
                router.push('/gatewaySessions/' + sessionCreated?.result?._id)
                setIsLoading(false)
            } else {
                throw new Error(await res.json())
            }
        } catch (error) {
            setIsLoading(false)
            addToast('Erro ao cadastrar sessão', 'error')
            console.error('An unexpected error happened occurred:', error)
        }
    }

    console.log(formData.userIds)
    function handleAddUser(user) {
        if (!user) {
            return false
        }
        if (formData.userIds.map(userSelected => userSelected._id).indexOf(user._id) >= 0) {
            return true
        }
        setFieldValue('userIds', [...formData.userIds, user])
    }

    function handleRemoveUser(user) {
        setFieldValue('userIds', formData.userIds.filter(userSelected => userSelected._id !== user._id))
    }


    return (
        <div className="">
            <button className="flex justify-between items-center space-x-1 cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap focus:shadow-sm" onClick={() => setNewGatewaySessionOpen(!newGatewaySessionOpen)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                <p>Nova sessão</p>
            </button>
            <div className={`fixed w-full h-full inset-0 transition-opacity duration-100 ${newGatewaySessionOpen ? 'z-50 opacity-100' : '-z-1 opacity-0'}`} onClick={() => setNewGatewaySessionOpen(false)}>
                <div class="absolute w-full h-full bg-gray-900 opacity-50" />
                <div class="modal-content absolute bg-white w-full md:w-11/12 md:mx-auto md:rounded md:shadow-lg z-50" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col justify-between p-3 space-y-3 h-full overflow-y-auto">
                        <div className="">
                            <div className="flex justify-between items-center mb-3">
                                <div className="">
                                    <p className="font-bold text-xl">Nova sessão</p>
                                </div>
                                <div className="cursor-pointer" onClick={() => setNewGatewaySessionOpen(false)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                                <div className="flex flex-col space-y-3">
                                    <Input 
                                        name="name" 
                                        type="text" 
                                        label="Nome da Sessão" 
                                        required 
                                        onChange={e => setFieldValue('name', e.target.value)}
                                        value={formData.name}
                                    />
                                    <FormSection
                                        title="Operadores"
                                        infos={
                                            <div className="flex flex-col space-y-3">
                                                <p className="text-gray-800">
                                                    Defina os operadores desta sessão.
                                                </p>
                                            </div>
                                        }
                                    >

                                        <SearchUser onSuccess={handleAddUser} />
                                        <div className="border rounded p-6 mb-6">
                                            {formData.userIds.length > 0 ? (
                                                <>
                                                    {formData.userIds.map((user, userIndex) => (
                                                        <div className="flex rounded border p-3 justify-between items-center mb-3">
                                                            <div className="">
                                                                <p className="text-md">{user.profile.name}</p>
                                                                <p className="text-sm text-gray-800">{user.email}</p>
                                                            </div>
                                                            <div className="flex justify-end">
                                                                <button onClick={() => handleRemoveUser(user)} className="btn btn-red">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                                        <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                                                                    </svg>

                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>
                                            ) : (
                                                <p className="text-center text-gray-800">
                                                    Nenhum usuário adicionado
                                                </p>
                                            )}
                                        </div>
                                        {renderError('userIds')}
                                    </FormSection>
                                </div>
                                <div className="flex space-x-3 justify-between mt-3">
                                    <div className="flex flex-col justify-between p-3 space-y-3 h-96 overflow-y-auto box w-full">
                                        <p className="font-bold">
                                            Eventos
                                        </p>
                                        <Input 
                                            size="s" 
                                            name="search" 
                                            type="text" 
                                            label={(
                                                <div className="flex space-x-2 items-center">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                                    </svg>
                                                    <p>Buscar eventos...</p>
                                                </div>
                                            )} 
                                            onKeyDown={e => e.key === 'Enter' && handleSearch(e)}
                                        />
                                        <div className="overflow-y-auto flex-grow flex-shrink h-full">
                                            <div className="flex flex-col h-full">
                                                { parties ? parties.result.map(party => (
                                                    <div onClick={() => handleSelectParty(party)}>
                                                        <EventItem event={party} active={selectedEvents.indexOf(party._id) >=0} />
                                                    </div>
                                                )) : <Loader />}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-between p-3 space-y-3 h-96 overflow-y-auto box w-full">
                                        <p className="font-bold">
                                            Lotes
                                        </p>
                                        <div className="overflow-y-auto flex-grow flex-shrink">
                                            <div className="flex flex-col h-full">
                                                { ticketTypes?.result?.map(ticketType => (
                                                    <div onClick={() => handleSelectTicketType(ticketType)}>
                                                        <TicketTypeItem ticketType={ticketType} active={selectedTicketTypes.indexOf(ticketType._id) >=0} />
                                                    </div>
                                                ))}
                                                { !ticketTypes && selectedEvents.length > 0 && <Loader />}
                                                { selectedEvents.length === 0 && (
                                                    <div className="flex items-center justify-center h-full">
                                                        <p>Nenhum evento selecionado</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="">
                                    {isLoading ? <Loader /> : (
                                        <button type="button" onClick={handleSubmit} disabled={isLoading} className="btn btn-primary w-full">Salvar</button>
                                    )}
                                </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

}
